import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import ConditionReviewList from 'app/pages/loanApplication/tabs/aiActivity/ExecutionDetailDrawer/StepDetailPane/StepPendingToolCall/ConditionReviewList';
import {
  buildAdjustSummary,
  CONDITION_REVIEW_VARIANT,
} from 'app/pages/loanApplication/tabs/aiActivity/ExecutionDetailDrawer/StepDetailPane/StepPendingToolCall/ConditionReviewList/ConditionReviewList.constants';
import useConditionReview from 'app/pages/loanApplication/tabs/aiActivity/ExecutionDetailDrawer/StepDetailPane/StepPendingToolCall/hooks/useConditionReview';
import useResolvedLibraryConditions from 'app/pages/loanApplication/tabs/aiActivity/ExecutionDetailDrawer/StepDetailPane/StepPendingToolCall/hooks/useResolvedLibraryConditions';
import {
  CONDITION_TOOL_NAMES,
  PENDING_TOOL_CALL_TOOL,
} from 'app/pages/loanApplication/tabs/aiActivity/ExecutionDetailDrawer/StepDetailPane/StepPendingToolCall/StepPendingToolCall.constants';
import {
  extractConditions,
  extractLibraryConditions,
  parseToolArgumentsObject,
} from 'app/pages/loanApplication/tabs/aiActivity/ExecutionDetailDrawer/StepDetailPane/StepPendingToolCall/StepPendingToolCall.utils';
import { frontendToolHandler } from 'app/services/aiChat';
import AiConfirmCard from 'app/shared/components/AiConfirmCard';
import { WarningFilledIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { ToolConfirmationStatus, ToolSlaGroupMode } from 'constants/aiChat';

import { approveToolCallAction, rejectToolCallAction } from 'store/aiChat/actions';
import { selectAiChatToolSlaSetting } from 'store/aiChat/selectors';

import { AiChatTestIds } from '../../../../AiChat.testIds';
import {
  TOOL_CONFIRMATION_FLOW,
  TOOL_CONFIRMATION_LABELS,
} from '../../ToolConfirmationCard.constants';
import { anyValuePropType } from '../../ToolConfirmationCard.propTypes';
import {
  adjustSummaryStyles,
  cardContainerStyles,
  contentStyles,
  detailStyles,
  headerStyles,
  iconContainerStyles,
  titleStyles,
} from '../../ToolConfirmationCard.styles';
import { buildToolConfirmationSummary } from '../../ToolConfirmationCard.utils';
import CancelledStatus from '../CancelledStatus';
import ExecutedStatus from '../ExecutedStatus';
import FailedStatus from '../FailedStatus';
import TimedOutStatus from '../TimedOutStatus';

import { TOOL_CONFIRM_LABELS } from './ToolConfirmationEntry.constants';

const EMPTY_CONDITIONS = [];

const ToolConfirmationEntry = ({
  messageId,
  confirmation,
  flow = TOOL_CONFIRMATION_FLOW.BACKEND,
}) => {
  const dispatch = useDispatch();

  const { toolCallId, toolName, friendlyName, toolArguments, status, result, error } = confirmation;

  const isFrontendFlow = flow === TOOL_CONFIRMATION_FLOW.FRONTEND;
  const isApproving = status === ToolConfirmationStatus.APPROVING;
  const isRejecting = status === ToolConfirmationStatus.REJECTING;

  const adhocConditions = useMemo(
    () =>
      toolName === PENDING_TOOL_CALL_TOOL.ADD_ADHOC_CONDITIONS
        ? extractConditions(parseToolArgumentsObject(toolArguments))
        : EMPTY_CONDITIONS,
    [toolName, toolArguments],
  );
  const libraryConditions = useMemo(
    () =>
      toolName === PENDING_TOOL_CALL_TOOL.ADD_CONDITIONS_FROM_LIBRARY
        ? extractLibraryConditions(parseToolArgumentsObject(toolArguments))
        : EMPTY_CONDITIONS,
    [toolName, toolArguments],
  );
  const resolvedLibraryConditions = useResolvedLibraryConditions(libraryConditions);
  const conditions = adhocConditions.length > 0 ? adhocConditions : resolvedLibraryConditions;
  const isItemized = !isFrontendFlow && CONDITION_TOOL_NAMES.has(toolName) && conditions.length > 0;
  const review = useConditionReview(conditions);

  const slaSetting = useSelector(state => selectAiChatToolSlaSetting(state, toolName));
  const isSlaSelectionRequired =
    !isFrontendFlow && slaSetting?.mode === ToolSlaGroupMode.PROMPT_USER;

  const labels = useMemo(
    () => ({
      ...TOOL_CONFIRM_LABELS,
      approve: isItemized
        ? TOOL_CONFIRMATION_LABELS.APPROVE_AND_ADD({ count: review.summary.addCount })
        : TOOL_CONFIRM_LABELS.approve,
      dismiss: isItemized ? TOOL_CONFIRMATION_LABELS.DECLINE_ALL : TOOL_CONFIRM_LABELS.dismiss,
    }),
    [isItemized, review.summary.addCount],
  );

  const adjustSummary =
    isItemized && review.hasAnyAction ? buildAdjustSummary(review.summary) : null;

  const onSubmit = useCallback(
    selectedSlaGroupId => {
      if (isFrontendFlow) {
        frontendToolHandler.confirmToolExecution();

        return;
      }

      dispatch(
        approveToolCallAction({
          messageId,
          toolCallId,
          selectedSlaGroupId,
          conditionDecisions: isItemized ? review.buildConditionDecisions() : null,
        }),
      );
    },
    [dispatch, isFrontendFlow, isItemized, messageId, review, toolCallId],
  );

  const onDismiss = useCallback(
    reason => {
      if (isFrontendFlow) {
        frontendToolHandler.rejectToolExecution(reason);

        return;
      }

      dispatch(
        rejectToolCallAction({
          messageId,
          toolCallId,
          reason,
        }),
      );
    },
    [dispatch, isFrontendFlow, messageId, toolCallId],
  );

  const summary = useMemo(
    () => buildToolConfirmationSummary({ toolName, friendlyName, toolArguments }),
    [toolName, friendlyName, toolArguments],
  );

  const detail = isItemized
    ? TOOL_CONFIRMATION_LABELS.CONDITIONS_INTRO({ count: conditions.length })
    : summary.detail;

  const isPendingLike =
    status === ToolConfirmationStatus.PENDING ||
    status === ToolConfirmationStatus.APPROVING ||
    status === ToolConfirmationStatus.REJECTING;

  return (
    <Box data-testid={AiChatTestIds.TOOL_CONFIRMATION_CARD} sx={cardContainerStyles}>
      <Box sx={headerStyles}>
        <Box sx={iconContainerStyles}>
          <WarningFilledIcon size={20} />
        </Box>
        <Box sx={contentStyles}>
          <Typography
            data-testid={AiChatTestIds.TOOL_CONFIRMATION_CARD_TITLE}
            sx={titleStyles}
            variant="medium14"
          >
            {summary.headline}
          </Typography>
          {detail ? (
            <Typography
              data-testid={AiChatTestIds.TOOL_CONFIRMATION_CARD_DETAIL}
              sx={detailStyles}
              variant="regular12"
            >
              {detail}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {isItemized ? (
        <ConditionReviewList
          conditions={conditions}
          review={review}
          variant={CONDITION_REVIEW_VARIANT.CHAT}
        />
      ) : null}
      {isPendingLike ? (
        <>
          {adjustSummary ? (
            <Typography
              data-testid={AiChatTestIds.TOOL_CONFIRMATION_CARD_ADJUST_SUMMARY}
              sx={adjustSummaryStyles}
              variant="regular12"
            >
              {adjustSummary}
            </Typography>
          ) : null}
          <AiConfirmCard
            entityId={toolCallId}
            isSlaSelectionRequired={isSlaSelectionRequired}
            isSubmitting={isApproving || isRejecting}
            labels={labels}
            onDismiss={onDismiss}
            onSubmit={onSubmit}
          />
        </>
      ) : null}
      {status === ToolConfirmationStatus.EXECUTED ? <ExecutedStatus result={result} /> : null}
      {status === ToolConfirmationStatus.FAILED ? <FailedStatus error={error} /> : null}
      {status === ToolConfirmationStatus.CANCELLED ? (
        <CancelledStatus reason={confirmation.reason} />
      ) : null}
      {status === ToolConfirmationStatus.TIMED_OUT ? <TimedOutStatus /> : null}
    </Box>
  );
};

ToolConfirmationEntry.propTypes = {
  messageId: PropTypes.string.isRequired,
  confirmation: PropTypes.shape({
    toolCallId: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    toolName: PropTypes.string,
    friendlyName: PropTypes.string,
    toolArguments: anyValuePropType,
    policy: PropTypes.string,
    result: anyValuePropType,
    error: PropTypes.string,
    reason: PropTypes.string,
  }).isRequired,
  flow: PropTypes.oneOf([TOOL_CONFIRMATION_FLOW.BACKEND, TOOL_CONFIRMATION_FLOW.FRONTEND]),
};

export default ToolConfirmationEntry;
